/**
 * WebGL Capabilities Detection
 * 
 * Detects WebGL support level and available features for graceful degradation.
 */

export interface WebGLCapabilities {
  webgl2: boolean;
  webgl1: boolean;
  maxTextureSize: number;
  maxCubeMapSize: number;
  maxAnisotropy: number;
  floatTextures: boolean;
  halfFloatTextures: boolean;
  depthTextures: boolean;
  drawBuffers: boolean;
  instancing: boolean;
  vertexArrayObjects: boolean;
}

let cachedCapabilities: WebGLCapabilities | null = null;

/**
 * Detect WebGL capabilities
 * Results are cached for performance
 */
export function detectCapabilities(): WebGLCapabilities {
  if (cachedCapabilities) {
    return cachedCapabilities;
  }

  const canvas = document.createElement('canvas');
  
  // Try WebGL2 first
  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = 
    canvas.getContext('webgl2') as WebGL2RenderingContext | null;
  
  const webgl2 = gl !== null;
  
  // Fall back to WebGL1
  if (!gl) {
    gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
  }

  if (!gl) {
    // No WebGL support at all
    cachedCapabilities = {
      webgl2: false,
      webgl1: false,
      maxTextureSize: 0,
      maxCubeMapSize: 0,
      maxAnisotropy: 0,
      floatTextures: false,
      halfFloatTextures: false,
      depthTextures: false,
      drawBuffers: false,
      instancing: false,
      vertexArrayObjects: false,
    };
    return cachedCapabilities;
  }

  // Get max anisotropy from extension
  const anisotropyExt = gl.getExtension('EXT_texture_filter_anisotropic') ||
    gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic') ||
    gl.getExtension('MOZ_EXT_texture_filter_anisotropic');
  
  const maxAnisotropy = anisotropyExt 
    ? gl.getParameter(anisotropyExt.MAX_TEXTURE_MAX_ANISOTROPY_EXT) 
    : 0;

  cachedCapabilities = {
    webgl2,
    webgl1: !webgl2,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
    maxCubeMapSize: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
    maxAnisotropy,
    floatTextures: webgl2 || !!gl.getExtension('OES_texture_float'),
    halfFloatTextures: webgl2 || !!gl.getExtension('OES_texture_half_float'),
    depthTextures: webgl2 || !!gl.getExtension('WEBGL_depth_texture'),
    drawBuffers: webgl2 || !!gl.getExtension('WEBGL_draw_buffers'),
    instancing: webgl2 || !!gl.getExtension('ANGLE_instanced_arrays'),
    vertexArrayObjects: webgl2 || !!gl.getExtension('OES_vertex_array_object'),
  };

  return cachedCapabilities;
}

/**
 * Check if WebGL is supported
 */
export function isWebGLSupported(): boolean {
  const caps = detectCapabilities();
  return caps.webgl1 || caps.webgl2;
}

/**
 * Check if WebGL2 is supported
 */
export function isWebGL2Supported(): boolean {
  return detectCapabilities().webgl2;
}

/**
 * Get recommended texture size based on device capabilities
 */
export function getRecommendedTextureSize(): number {
  const caps = detectCapabilities();
  // Cap at 2048 for performance, even if device supports more
  return Math.min(caps.maxTextureSize, 2048);
}
